import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { MatSpinner } from '@angular/material/progress-spinner';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { getChildObjects, getFormRoot } from '@intellio/shared/utils';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { RelateAppsListComponent } from '../components/relate-apps-list/relate-apps-list.component';
import type { RelateAppsListItem } from '../components/relate-apps-list/relate-apps-list.component';

interface ApplicationLookupResult {
  totalApplications: number;
  applications: RelateAppsListItem[];
  rebateTypeIneligibleApplications?: RelateAppsListItem[];
  message: string;
}

const GENERATION_ASSOCIATED_APP_ID = 'generation_associated_app_id';
const STORAGE_ASSOCIATED_APP_ID = 'storage_associated_app_id';
const QUEUE_NUMBER_UDF_ID = 'QueueNumber';

interface SelectedApplicationUdfConfig {
  fieldKey: string;
  udfId: string;
  udfName?: string;
}

type SelectedApplicationSource = 'generation' | 'storage' | 'prepopulation';

interface ProjectIdMappingConfig {
  targetFieldKey: string;
  source?: SelectedApplicationSource;
  fallbackSource?: SelectedApplicationSource;
}

interface QueueNumberMappingConfig {
  targetFieldKey: string;
  source?: SelectedApplicationSource;
  fallbackSource?: SelectedApplicationSource;
}

@Component({
  selector: 'itc-relate-apps-drawer-field',
  styleUrls: ['./relate-apps-drawer-field.component.scss'],
  template: `
    <div
      class="relate-apps-drawer-field"
      *ngIf="lookupResult$ | async as lookupResult; else loadingOrError"
    >
      <div *ngIf="errorObject && !props.isLoading" class="col-12 text-center">
        <div class="alert alert-danger">
          {{ errorObject.message }}
        </div>
      </div>
      <div
        *ngIf="formControl.hasError('rebateTypeMismatch')"
        class="relate-apps-drawer-field__validation-error ng-error"
        role="alert"
        i18n
      >
        Application must match selected rebate type
      </div>
      <itc-relate-apps-list
        *ngIf="selectedApplications.length > 0"
        class="relate-apps-drawer-field__added-list"
        [apps]="selectedApplications"
        [selectedAppIds]="selectedApplicationIds"
        [showRemoveAction]="true"
        (appRemoved)="removeCard($event)"
      ></itc-relate-apps-list>
      <ng-container *ngIf="props.isLoading; else currentLookupResults">
        <div class="in-place-spinner-wrapper">
          <mat-spinner></mat-spinner>
        </div>
      </ng-container>
      <ng-template #currentLookupResults>
        <ng-container *ngIf="!errorObject">
          <div *ngIf="lookupResult.message">
            <div class="alert alert-info">
              {{ lookupResult.message }}
            </div>
          </div>
          <div *ngIf="visibleApplications.length > 0; else noResults">
            <div class="relate-apps-drawer-field__result-count">
              {{ visibleApplications.length }} results found. Please select from
              the list below
            </div>
            <itc-relate-apps-list
              [apps]="visibleApplications"
              [selectedAppIds]="selectedApplicationIds"
              (appSelected)="selectCard($event)"
            ></itc-relate-apps-list>
          </div>
        </ng-container>
      </ng-template>
    </div>
    <ng-template #loadingOrError>
      <ng-container *ngIf="props.isLoading; else loadingError">
        <div class="in-place-spinner-wrapper">
          <mat-spinner></mat-spinner>
        </div>
      </ng-container>
      <ng-template #loadingError>
        <div *ngIf="errorObject" class="col-12 text-center">
          <div class="alert alert-danger">
            {{ errorObject.message }}
          </div>
        </div>
      </ng-template>
    </ng-template>
    <ng-template #noResults>
      <div>
        {{ noResultsText }}
      </div>
    </ng-template>
  `,
  imports: [CommonModule, MatSpinner, RelateAppsListComponent],
})
export class RelateAppsDrawerFieldComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  errorObject = null;
  control: UntypedFormControl;
  lookupResult$: Observable<ApplicationLookupResult>;
  noResultsText: string;
  selectedApplicationIds: string[] = [];
  selectedApplications: RelateAppsListItem[] = [];
  visibleApplications: RelateAppsListItem[] = [];
  private lookupApplications: RelateAppsListItem[] = [];
  private rebateTypeIneligibleApplicationIds = new Set<string>();
  private isRebateTypeLookup = false;
  private prepopulationSourceAppId: string = null;

  private readonly rebateTypeEligibilityValidator: ValidatorFn = (control) => {
    if (!this.isRebateTypeLookup) {
      return null;
    }

    if (this.props.isLoading) {
      return { rebateTypeLookupPending: true };
    }
    if (this.props.errorObject) {
      return { rebateTypeLookupFailed: true };
    }

    const selectedApplicationIds = Array.isArray(control.value)
      ? control.value
      : [];
    const hasMismatchedApplication = selectedApplicationIds.some((id) =>
      this.rebateTypeIneligibleApplicationIds.has(id)
    );

    return hasMismatchedApplication ? { rebateTypeMismatch: true } : null;
  };

  ngOnInit() {
    this.control = this?.formControl as UntypedFormControl;
    this.isRebateTypeLookup = getChildObjects(
      getFormRoot(this.field),
      'key'
    ).some(
      (field) =>
        field.key === 'rebate_type' &&
        field.type === 'select' &&
        field.className !== 'invisible'
    );
    if (this.isRebateTypeLookup) {
      this.control.addValidators(this.rebateTypeEligibilityValidator);
    }
    this.noResultsText = this.props.noResultsText;
    this.lookupResult$ = this.props.lookupResult$;
    this.lookupResult$ = this.lookupResult$.pipe(
      tap((lookupResult) => {
        this.errorObject = this.props.errorObject ?? null;
        this.lookupApplications = lookupResult?.applications ?? [];
        this.rebateTypeIneligibleApplicationIds = new Set(
          (lookupResult?.rebateTypeIneligibleApplications ?? [])
            .map((app) => app.id)
            .filter((id): id is string => Boolean(id))
        );
        this.updateVisibleApplications();
        this.props.isLoading = false;
        if (this.isRebateTypeLookup) {
          this.control.updateValueAndValidity({ emitEvent: false });
        }
      }),
      catchError((err) => {
        this.props.isLoading = false;
        this.errorObject = err;
        if (this.isRebateTypeLookup) {
          this.control.updateValueAndValidity({ emitEvent: false });
        }
        return throwError(err);
      })
    );
  }

  selectCard(card: RelateAppsListItem) {
    if (!this.canSelectCard(card)) {
      return;
    }

    this.selectedApplications = [...this.selectedApplications, card];
    this.updateSelectionState();
  }

  removeCard(card: RelateAppsListItem) {
    this.selectedApplications = this.selectedApplications.filter(
      (selectedApp) => selectedApp.id !== card.id
    );
    this.updateSelectionState();
  }

  private updateSelectionState() {
    this.selectedApplicationIds = this.selectedApplications.map((app) => app.id);
    this.control.setValue(
      this.selectedApplicationIds.length > 0
        ? this.selectedApplicationIds
        : null
    );
    this.updateAssociatedApplicationFields();
    this.updateSelectedApplicationUdfField();
    this.updatePrepopulationSource();
    this.syncSelectedApplicationProjectId();
    this.syncSelectedApplicationQueueNumber();
    this.updateVisibleApplications();
  }

  private updateVisibleApplications() {
    this.visibleApplications = this.lookupApplications
      .filter(
        (app) =>
          !this.selectedApplications.some(
            (selectedApp) => selectedApp.id === app.id
          )
      )
      .map((app) => this.applySelectionRules(app));
  }

  private applySelectionRules(app: RelateAppsListItem): RelateAppsListItem {
    if (app.disabled) {
      return app;
    }

    const disabledReason = this.getSelectionRestrictionReason(app);
    if (!disabledReason) {
      return app;
    }

    return {
      ...app,
      disabled: true,
      disabledReason,
    };
  }

  private canSelectCard(card: RelateAppsListItem): boolean {
    return !card.disabled && !this.getSelectionRestrictionReason(card);
  }

  private getSelectionRestrictionReason(app: RelateAppsListItem): string {
    if (this.selectedApplications.length === 0) {
      return '';
    }

    if (this.selectedApplications.length >= 2) {
      return 'Only one storage-only DER application and one generation DER application can be linked.';
    }

    if (this.isStorageOnlyApp(app) && this.hasStorageOnlyApp()) {
      return 'Only one storage-only DER application can be linked.';
    }

    if (!this.isStorageOnlyApp(app) && this.hasGenerationApp()) {
      return 'Only one generation DER application can be linked.';
    }

    return '';
  }

  private updateAssociatedApplicationFields() {
    const storageApp = this.selectedApplications.find((app) =>
      this.isStorageOnlyApp(app)
    );
    const generationApp = this.selectedApplications.find(
      (app) => !this.isStorageOnlyApp(app)
    );

    this.setFieldValue(STORAGE_ASSOCIATED_APP_ID, storageApp?.id ?? null);
    this.setFieldValue(GENERATION_ASSOCIATED_APP_ID, generationApp?.id ?? null);
  }

  private updateSelectedApplicationUdfField() {
    const config = this.props
      ['selectedApplicationUdf'] as SelectedApplicationUdfConfig;

    if (!config?.fieldKey || !config?.udfId) {
      return;
    }

    const udfValue = this.selectedApplications
      .map((app) => this.getSelectedApplicationUdfValue(app, config))
      .find((value) => value !== null && value !== undefined && value !== '');

    this.setFieldValue(config.fieldKey, udfValue ?? null);
  }

  private getSelectedApplicationUdfValue(
    app: RelateAppsListItem,
    config: SelectedApplicationUdfConfig
  ): string | null {
    const udfDetails = app.udfs ?? app.applicationUDFDetails ?? [];
    const selectedUdf = udfDetails.find(
      (udf) =>
        udf.id === config.udfId ||
        udf.udfId === config.udfId ||
        (config.udfName && udf.name === config.udfName)
    );

    return selectedUdf?.stringValue ?? selectedUdf?.value ?? null;
  }

  private syncSelectedApplicationProjectId() {
    const config = this.props['projectIdMapping'] as ProjectIdMappingConfig;

    if (!config?.targetFieldKey) {
      return;
    }

    const selectedApp = this.getSelectedApplicationBySource(config.source)
      ?? this.getSelectedApplicationBySource(config.fallbackSource);

    this.setFieldValue(config.targetFieldKey, selectedApp?.projectId ?? null);
  }

  private syncSelectedApplicationQueueNumber() {
    const config = this.props[
      'queueNumberMapping'
    ] as QueueNumberMappingConfig;

    if (!config?.targetFieldKey) {
      return;
    }

    const selectedApp = this.getSelectedApplicationBySource(config.source)
      ?? this.getSelectedApplicationBySource(config.fallbackSource);
    const queueNumber = selectedApp
      ? this.getSelectedApplicationUdfValue(selectedApp, {
          fieldKey: config.targetFieldKey,
          udfId: QUEUE_NUMBER_UDF_ID,
        })
      : null;

    this.setFieldValue(config.targetFieldKey, queueNumber ?? null);
  }

  private updatePrepopulationSource() {
    const sourceApp = this.getPrepopulationSourceApp();

    if (sourceApp?.id === this.prepopulationSourceAppId) {
      return;
    }

    if (!sourceApp && !this.prepopulationSourceAppId) {
      return;
    }

    this.prepopulationSourceAppId = sourceApp?.id ?? null;
    this.props.onClick?.(sourceApp ?? {});
  }

  private getPrepopulationSourceApp(): RelateAppsListItem | undefined {
    return (
      this.selectedApplications.find((app) => this.isStorageOnlyApp(app)) ??
      this.selectedApplications.find((app) => !this.isStorageOnlyApp(app))
    );
  }

  private getSelectedApplicationBySource(
    source: SelectedApplicationSource = 'prepopulation'
  ): RelateAppsListItem | undefined {
    switch (source) {
      case 'generation':
        return this.selectedApplications.find((app) =>
          !this.isStorageOnlyApp(app)
        );
      case 'storage':
        return this.selectedApplications.find((app) =>
          this.isStorageOnlyApp(app)
        );
      default:
        return this.getPrepopulationSourceApp();
    }
  }

  private hasStorageOnlyApp(): boolean {
    return this.selectedApplications.some((app) => this.isStorageOnlyApp(app));
  }

  private hasGenerationApp(): boolean {
    return this.selectedApplications.some(
      (app) => !this.isStorageOnlyApp(app)
    );
  }

  private isStorageOnlyApp(app: RelateAppsListItem): boolean {
    const energySourceIds = this.getEnergySourceIds(app);
    return (
      energySourceIds.length === 1 &&
      ['storage', 'standalonestorage'].includes(energySourceIds[0].toLowerCase())
    );
  }

  private getEnergySourceIds(app: RelateAppsListItem): string[] {
    if (app.energySourceIds?.length) {
      return app.energySourceIds;
    }

    return app.energySourceId == null ? [] : [app.energySourceId];
  }

  private setFieldValue(key: string, value: string) {
    this.form?.get(key)?.setValue(value);
  }
}
